/**
 * UatDecisionPanel — the approval gate surface inside the service detail
 * overlay (ADR-075 steps 3–4; scenario-app-uat-gate, role-uat-approver).
 *
 * Shown when a tile carries an active UAT deployment. Displays the deployment
 * number, date and version. For holders of the uat-approver realm role it
 * offers the two decisions:
 *   - Approve      → the app version goes live for store users
 *   - Push back    → the app version returns to the makers, with a comment
 *                    (mandatory — enforced here for UX and server-side as the
 *                    actual gate)
 *
 * Language rule (role-uat-approver is non-technical): no git / registry /
 * secret-manager vocabulary anywhere on this surface — only "deployment",
 * "version", "date".
 */

import { useState } from 'react';
import { t } from '../i18n';
import { useAuth } from '../auth/KeycloakProvider';
import { useUatDecision } from '../hooks/useUatDecision';
import type { TileConfig } from '../config/schema';

// Realm roles that may decide (same Set pattern as useCatalog's
// PRIVATE_ALLOWED_ROLES — config-shaped constant, single place to change).
const UAT_APPROVER_ROLES = new Set(['uat-approver']);

interface UatDecisionPanelProps {
  tile: TileConfig;
  /** Called after a decision was accepted by the broker. */
  onDecided?: () => void;
}

export default function UatDecisionPanel({ tile, onDecided }: UatDecisionPanelProps) {
  const { token, user } = useAuth();
  const { state, decide } = useUatDecision(token);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState(false);

  const deployment = tile.deployment;
  if (!deployment || deployment.phase !== 'uat') return null;

  const isApprover = (user?.roles ?? []).some((r) => UAT_APPROVER_ROLES.has(r));

  const label = t('uat.deploymentLabel')
    .replace('{{no}}', String(deployment.deployNo))
    .replace('{{date}}', deployment.deployDate);

  async function submit(decision: 'approve' | 'pushback') {
    if (decision === 'pushback' && comment.trim().length === 0) {
      setCommentError(true);
      return;
    }
    setCommentError(false);
    const ok = await decide({
      appId: tile.id,
      deployNo: deployment!.deployNo,
      decision,
      comment: decision === 'pushback' ? comment.trim() : undefined,
    });
    if (ok) onDecided?.();
  }

  const busy = state.tag === 'submitting';

  return (
    <div
      style={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--dp-warn-300)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--dp-warn-050)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 'var(--fs-eyebrow)',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--dp-warn-100)',
            color: 'var(--dp-warn-700)',
          }}
        >
          {t('uat.badge')}
        </span>
        <span style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body-sm)',
          fontWeight: 600,
          color: 'var(--fg-1)',
        }}>
          {label}
        </span>
        {deployment.releaseTag && (
          <span style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            color: 'var(--fg-3)',
          }}>
            {t('uat.versionLabel').replace('{{version}}', deployment.releaseTag)}
          </span>
        )}
      </div>

      <p style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-body-sm)',
        color: 'var(--fg-2)',
        lineHeight: 'var(--lh-body-sm)',
        margin: 0,
      }}>
        {isApprover ? t('uat.approverIntro') : t('uat.testerIntro')}
      </p>

      {isApprover && state.tag !== 'done' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {showComment && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
              <textarea
                value={comment}
                onChange={(e) => { setComment(e.target.value); if (e.target.value.trim()) setCommentError(false); }}
                placeholder={t('uat.commentPlaceholder')}
                rows={3}
                aria-label={t('uat.commentPlaceholder')}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  padding: 'var(--sp-2)',
                  borderRadius: 'var(--radius-sm)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: commentError ? 'var(--dp-crimson-600)' : 'var(--border-default)',
                  background: 'var(--bg-surface)',
                  color: 'var(--fg-1)',
                  resize: 'vertical',
                }}
              />
              {commentError && (
                <span style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--dp-crimson-600)',
                }}>
                  {t('uat.commentRequired')}
                </span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
            {!showComment && (
              <button
                className="dp-btn dp-btn--primary"
                disabled={busy}
                onClick={() => submit('approve')}
              >
                {busy && state.tag === 'submitting' && state.decision === 'approve'
                  ? t('uat.submitting') : t('uat.approve')}
              </button>
            )}
            {!showComment && (
              <button
                className="dp-btn dp-btn--ghost"
                disabled={busy}
                onClick={() => setShowComment(true)}
              >
                {t('uat.pushBack')}
              </button>
            )}
            {showComment && (
              <>
                <button
                  className="dp-btn dp-btn--primary"
                  disabled={busy}
                  onClick={() => submit('pushback')}
                >
                  {busy && state.tag === 'submitting' && state.decision === 'pushback'
                    ? t('uat.submitting') : t('uat.pushBackConfirm')}
                </button>
                <button
                  className="dp-btn dp-btn--ghost"
                  disabled={busy}
                  onClick={() => { setShowComment(false); setCommentError(false); }}
                >
                  {t('destructive.cancel')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {state.tag === 'done' && (
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body-sm)',
          fontWeight: 600,
          color: 'var(--dp-teal-700)',
          margin: 0,
        }}>
          {state.decision === 'approve' ? t('uat.approvedDone') : t('uat.pushedBackDone')}
        </p>
      )}

      {state.tag === 'error' && (
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--dp-crimson-600)',
          margin: 0,
        }}>
          {t(state.message === 'stale' ? 'uat.errorStale'
            : state.message === 'forbidden' ? 'uat.errorForbidden'
            : 'uat.errorGeneric')}
        </p>
      )}
    </div>
  );
}
